import { lazy, ReactNode, Suspense } from 'react'

import { buildCdnImageUrl } from '@susu/order-list'
import { Button } from '@susu/ui/components'

import { useCustomerHub } from '../contexts/CustomerHubContext'

// Dynamic import for framer-motion to reduce initial bundle size
const MotionDiv = lazy(() =>
  import('framer-motion').then((mod) => ({ default: mod.motion.div })),
)

interface ImageConfig {
  src: string
  alt: string
  className?: string
  rotation?: number
  position?: { left: number; top: number }
  size?: { width: number; height: number }
}

export interface EmptyStateProps {
  // Content
  title: string | ReactNode
  description?: string | ReactNode

  // Visual customization (optional)
  images?: ImageConfig[]

  // Actions (optional)
  primaryAction?: {
    label: string
    onClick: () => void
    variant?: 'filled' | 'outlined'
    testId?: string
  }
  secondaryAction?: {
    label: string
    onClick: () => void
    testId?: string
  }

  // Layout control
  className?: string
  testId?: string
}

const EMPTY_STATE_IMAGE_ROOT =
  'v1769083147/radical-personal/Customer%20Hub/Empty%20state'

const emptyStateImage = (n: number) =>
  buildCdnImageUrl(`${EMPTY_STATE_IMAGE_ROOT}/customer_hub_image-${n}.png`, {
    width: 250,
    fill: false,
  })

const DEFAULT_IMAGES: ImageConfig[] = [
  {
    src: emptyStateImage(3),
    alt: '',
    rotation: -6,
    position: { left: 3, top: 1 },
  },
  {
    src: emptyStateImage(2),
    alt: '',
    rotation: 4,
    position: { left: 45, top: 8 },
  },
  {
    src: emptyStateImage(1),
    alt: '',
    rotation: 0,
    position: { left: 24, top: 27 },
  },
]

export default function EmptyState({
  title,
  description,
  images = DEFAULT_IMAGES,
  primaryAction,
  secondaryAction,
  className = '',
  testId,
}: EmptyStateProps) {
  const { disableAnimations } = useCustomerHub()

  return (
    <div
      className={`flex flex-col items-center lg:items-start px-16 lg:px-0 xl:px-40 3xl:pl-[116px] ${className}`}
      data-testid={testId}
    >
      <div className="flex flex-col gap-20 xl:gap-12 items-center max-w-[326px] lg:max-xl:max-w-[276px]">
        {/* Image Stack */}
        {images.length > 0 && (
          <div className="relative isolate mt-20 w-[230px] h-[204px] xl:w-[330px] xl:h-[296px]">
            {disableAnimations ? (
              images.map((image, index) => {
                const rotation = image.rotation ?? 0
                const position = image.position ?? { left: 0, top: 0 }
                return (
                  <div
                    key={index}
                    className="absolute overflow-hidden rounded-lg border border-white bg-surface-secondary w-[120px] h-[144px] xl:w-[170px] xl:h-[204px]"
                    style={{
                      left: `${position.left}%`,
                      top: `${position.top}%`,
                      zIndex: index + 1,
                      transform: `rotate(${rotation}deg)`,
                    }}
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                      className="h-full w-full object-cover object-center"
                    />
                  </div>
                )
              })
            ) : (
              <Suspense fallback={null}>
                {images.map((image, index) => {
                  const rotation = image.rotation ?? 0
                  const position = image.position ?? { left: 0, top: 0 }

                  const delay = index * 0.08

                  // Different animation direction for each card
                  const getInitialPosition = () => {
                    switch (index) {
                      case 0:
                        return { x: -25, y: 0 } // from left
                      case 1:
                        return { x: 25, y: 0 } // from right
                      case 2:
                        return { x: 0, y: 25 } // from bottom
                      default:
                        return { x: 0, y: 0 }
                    }
                  }

                  const initialPosition = getInitialPosition()

                  return (
                    <MotionDiv
                      key={index}
                      className="absolute overflow-hidden rounded-lg border border-white bg-surface-secondary w-[120px] h-[144px] xl:w-[170px] xl:h-[204px]"
                      style={{
                        left: `${position.left}%`,
                        top: `${position.top}%`,
                        zIndex: index + 1,
                      }}
                      initial={{
                        x: initialPosition.x,
                        y: initialPosition.y,
                        rotate: rotation,
                        opacity: 0.8,
                      }}
                      animate={{
                        x: 0,
                        y: 0,
                        rotate: rotation,
                        opacity: 1,
                      }}
                      transition={{
                        duration: 0.25,
                        delay,
                        ease: 'easeOut',
                      }}
                    >
                      <img
                        src={image.src}
                        alt={image.alt}
                        loading="eager"
                        fetchPriority="high"
                        decoding="async"
                        className="h-full w-full object-cover object-center"
                      />
                    </MotionDiv>
                  )
                })}
              </Suspense>
            )}
          </div>
        )}

        {/* Title */}
        <div className="max-w-[326px] text-center font-sans text-base font-light leading-base text-text">
          {title}
        </div>

        {/* Description (optional) */}
        {description && (
          <div className="max-w-[326px] text-center font-sans text-sm font-light text-text-secondary">
            {description}
          </div>
        )}

        {/* Actions */}
        {(primaryAction || secondaryAction) && (
          <div className="flex flex-col gap-8">
            {primaryAction && (
              <Button
                size="l"
                variant={primaryAction.variant || 'filled'}
                onClick={primaryAction.onClick}
                className="body-s-fixed"
                data-testid={primaryAction.testId}
              >
                {primaryAction.label}
              </Button>
            )}
            {secondaryAction && (
              <Button
                size="l"
                variant="outlined"
                onClick={secondaryAction.onClick}
                className="body-s-fixed"
                data-testid={secondaryAction.testId}
              >
                {secondaryAction.label}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
